import React, { useEffect, useState } from 'react';
import { Form } from './index';
import { DatePicker } from './DatePicker';
import { getDateFormat, getLocale, getTimezoneFromCountry } from '../utils';
import { TimePicker } from './TimePicker';
import { Col, Row } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { DoubleLeftOutlined, DoubleRightOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';

const toIsoDateTime = (date, time, countryCode) => dayjs.tz(
  date.format('YYYY-MM-DD') + 'T' + time.format('HH:mm'),
  getTimezoneFromCountry(countryCode),
).toISOString();

const join = (date, time) => {
  if (!time) {
    return date;
  }
  return dayjs(date?.format('YYYY-MM-DD') + 'T' + time?.format('HH:mm'));
};

const DateTimePicker = ({
  value, onChange, countryCode, labels, disabledDate, disabledTime, hideTime, getDefaultTime,
}) => {
  const { i18n } = useTranslation();

  const { status } = Form.Item.useStatus();

  const [ date, setDate ] = useState(value);
  const [ time, setTime ] = useState(value);

  // When the time selection is hidden, the time part is chosen automatically.
  // Apply the default time to the initial value on mount so that values which are
  // never edited (e.g. an end date that already defaults to today) get the proper
  // default time (e.g. 23:59) instead of keeping the instant-now value.
  //
  // Deps are intentionally empty: this only fixes up the *initial* value
  // once on mount. Afterwards handleDateChange owns keeping the value correct.
  // getDefaultTime/onChange are recreated on every parent render, so listing them
  // would re-run this effect repeatedly and clobber the user's selection.
  useEffect(() => {
    if (hideTime && getDefaultTime && date) {
      const defaultTime = getDefaultTime(date);
      setTime(defaultTime);
      onChange(toIsoDateTime(date, defaultTime, countryCode));
    }
  }, []);

  const isTimeDisabled = (value, selectedTime) => {
    if (!disabledTime || !selectedTime) {
      return false;
    }
    const disabled = disabledTime(join(value, selectedTime));
    return disabled.disabledHours().includes(selectedTime.hour()) ||
      disabled.disabledMinutes().includes(selectedTime.minute());
  };

  const handleDateChange = value => {
    setDate(value);
    // The time of the previous date is kept only when it is allowed on the new date
    if (value && getDefaultTime && (hideTime || isTimeDisabled(value, time))) {
      const defaultTime = getDefaultTime(value);
      setTime(defaultTime);
      onChange(toIsoDateTime(value, defaultTime, countryCode));
    } else if (value && time) {
      onChange(toIsoDateTime(value, time, countryCode));
    }
  };

  const handleTimeChange = value => {
    setTime(value);
    if (value && date) {
      onChange(toIsoDateTime(date, value, countryCode));
    }
  };

  return <Row gutter={15}>
    <Col xxl={hideTime ? 24 : 16} xl={hideTime ? 24 : 13}>
      <Form.Item label={labels?.[0]} style={{ margin: 0 }}>
        <DatePicker
          value={date}
          format={getDateFormat(i18n)}
          locale={getLocale(i18n)}
          onChange={handleDateChange}
          inputReadOnly={true}
          placeholder={null}
          allowClear={false}
          disabledDate={disabledDate}
          className={status === 'error' ? 'ant-picker-status-error' : ''}
          nextIcon={<RightOutlined/>}
          superNextIcon={<DoubleRightOutlined/>}
          prevIcon={<LeftOutlined/>}
          superPrevIcon={<DoubleLeftOutlined/>}
        />
      </Form.Item>
    </Col>

    {!hideTime && <Col xxl={8} xl={11}>
      <Form.Item label={labels?.[1]} style={{ margin: 0 }}>
        <TimePicker
          value={time}
          format="HH:mm"
          locale={getLocale(i18n)}
          onChange={handleTimeChange}
          inputReadOnly={true}
          placeholder={null}
          allowClear={false}
          disabledTime={() => disabledTime(join(date, time))}
          disabled={!date}
          className={status === 'error' ? 'ant-picker-status-error' : ''}
        />
      </Form.Item>
    </Col>}
  </Row>;
};

export default DateTimePicker;
